import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type AllocationProposal,
  type AssignmentBoardView,
  type DayGenerated,
  assignTask,
  fetchAssignmentBoard,
  generateDayWork,
  proposeAllocation,
  setWorkSchedule,
} from '@hk/core'

/* The assignment board refreshes more slowly than the room board — 60 seconds against
   30. It is read while a supervisor is dragging cards around, and a board that
   rearranges itself under a half-finished decision is worse than one that is a minute
   behind. The refetch is also skipped entirely while an assignment is in flight. */

export const REFRESH_MS = 60_000

export interface AssignmentBoardState {
  data: AssignmentBoardView | undefined
  error: unknown
  isLoading: boolean
  /** The task currently being moved, so its card can show it is in flight. */
  movingTaskId: string | null
  assignError: unknown
  assign: (taskId: string, toUserId: string) => void
  refetch: () => void

  /* ── SPRINT 33: WHERE THE WORK COMES FROM ────────────────────────────────
     The board had nothing to show until somebody seeded a database. This is the
     button that fills it. */

  /** Sweep the hotel for today's work. Safe to press twice. */
  generate: () => void
  generating: boolean
  /**
   * Sprint 34 — set the time the sweep runs by itself, or null to turn it off.
   *
   * **IT RESOLVES TRUE WHEN THE SERVER TOOK IT**, which is the one thing on this
   * interface that does not follow `assign` and `generate`, and for a reason worth the
   * three lines: the caller is a DIALOG, and a dialog has to know whether to close.
   *
   * `mutate` is fire-and-forget, so a dialog built on it can only guess — close
   * optimistically and a refusal is dismissed along with the form that caused it, or
   * stay open and a successful save leaves the supervisor pressing Cancel on a form
   * that has already done its job. The boolean says which happened. The refusal itself
   * is NOT in the boolean: it is in `scheduleError`, where the dialog renders it, and
   * false means only "do not close".
   */
  setSchedule: (localMinute: number | null) => Promise<boolean>
  savingSchedule: boolean
  scheduleError: unknown
  /**
   * Forget a refusal.
   *
   * A mutation's error OUTLIVES THE FORM THAT CAUSED IT. react-query holds it until the
   * next `mutate`, so a supervisor who is refused, closes the dialog and opens it again
   * is met by the refusal from last time — sitting under an untouched form, describing
   * something they may already have corrected. Called when the form opens.
   */
  clearScheduleError: () => void
  /** What the last sweep did, held until the supervisor dismisses it — a summary that
   *  vanished on the next refetch would be one nobody finished reading. */
  generated: DayGenerated | null
  generateError: unknown
  dismissGenerated: () => void

  /* ── W-42 · THE SYSTEM PROPOSES AND THE SUPERVISOR APPROVES (Sprint 73) ────────
     The client's answer, in their own words. So the plan is HELD, drawn, and applied
     only on approval — never on the way to being computed. */

  /** The plan currently on screen, or null when none has been asked for. */
  proposal: AllocationProposal | null
  /** Work one out from the board as it stands. Nothing moves. */
  propose: () => void
  /** Put the current plan into effect, through the same per-task assignment as a drag. */
  approve: () => void
  /** Throw it away. Nothing was ever changed. */
  discard: () => void
  /** How many of the plan's placements have landed, while it is being applied. */
  approving: { done: number; total: number } | null
  approveError: unknown
}

export function useAssignmentBoard(propertyId: string): AssignmentBoardState {
  const client = useQueryClient()

  const query = useQuery({
    queryKey: ['assignment-board', propertyId],
    queryFn: ({ signal }) => fetchAssignmentBoard(propertyId, { signal }),
    refetchInterval: REFRESH_MS,
    placeholderData: (previous) => previous,
  })

  const mutation = useMutation({
    mutationFn: ({ taskId, toUserId }: { taskId: string; toUserId: string }) =>
      assignTask(taskId, { assignToUserId: toUserId }),

    // NOT optimistic. An assignment that appears to land and then silently reverts is
    // worse than one that takes a moment: the supervisor has already moved on, and the
    // room is now nobody's. The card shows it is in flight and the board re-reads.
    onSettled: () => client.invalidateQueries({ queryKey: ['assignment-board', propertyId] }),
  })

  const [generated, setGenerated] = useState<DayGenerated | null>(null)

  /* **THE PLAN IS STATE AND NOT A MEMO**, and that is the client's answer expressed in
     the shape of the code. A `useMemo` over the board would recompute the proposal on
     every refetch — so the plan a supervisor is reading would silently change under him
     between deciding and approving, and what he approved would not be what he read.
     Asking for it is an act; it stays as it was until he approves or discards it. */
  const [proposal, setProposal] = useState<AllocationProposal | null>(null)
  const [approving, setApproving] = useState<{ done: number; total: number } | null>(null)
  const [approveError, setApproveError] = useState<unknown>(null)

  const generation = useMutation({
    mutationFn: () => generateDayWork(propertyId),
    /* THE SUMMARY IS KEPT IN STATE RATHER THAN READ FROM THE MUTATION.
    
       `useMutation`'s `data` is cleared the moment another mutation runs — and the very
       next thing a supervisor does after generating the day is assign a card. The
       summary naming the rooms nobody can clean would vanish under the first drag,
       which is exactly when they need it. It is dismissed by hand instead. */
    onSuccess: (result) => {
      setGenerated(result)
      void client.invalidateQueries({ queryKey: ['assignment-board', propertyId] })
    },
  })

  /* **APPLIED ONE ROOM AT A TIME, THROUGH THE ASSIGNMENT THE BOARD ALREADY HAS.** There
     is no bulk endpoint and there should not be: a second way to assign a room is a second
     place for the rules to be enforced, and the first morning they disagreed a card would
     land somewhere the board would have refused.

     **IN SEQUENCE, AND IT STOPS AT THE FIRST REFUSAL.** Firing sixteen at once would put
     sixteen refusals on screen for one cause — a shift that ended, a room somebody else
     took — and leave the supervisor to work out which placements survived. Stopping leaves
     a board that is half the plan and wholly true, and the count says how far it got. */
  const approval = useMutation({
    mutationFn: async (plan: AllocationProposal) => {
      setApproveError(null)
      let done = 0
      setApproving({ done, total: plan.placements.length })

      for (const placement of plan.placements) {
        await assignTask(placement.task.taskId, { assignToUserId: placement.toUserId })
        done += 1
        setApproving({ done, total: plan.placements.length })
      }
    },
    onError: (error) => setApproveError(error),
    onSettled: () => {
      setApproving(null)
      /* **THE PLAN IS CLEARED EVEN WHEN IT FAILED PART-WAY.** What is left of it describes
         a board that no longer exists, and a stale plan is worse than none: it names rooms
         that have already moved. He asks again and reads a current one. */
      setProposal(null)
      void client.invalidateQueries({ queryKey: ['assignment-board', propertyId] })
    },
  })

  const schedule = useMutation({
    mutationFn: (localMinute: number | null) => setWorkSchedule(propertyId, localMinute),
    /* THE BOARD IS REFETCHED, NOT PATCHED. Changing the time also clears the "already ran
       today" stamp on the server — so `scheduledRunIsOverdue` may flip in the same breath,
       and a client that updated only the field it sent would draw a board that disagrees
       with the one it would get if it asked. *Use what the server says.* */
    onSuccess: () => client.invalidateQueries({ queryKey: ['assignment-board', propertyId] }),
  })

  return {
    data: query.data,
    error: query.error,
    isLoading: query.isLoading,
    movingTaskId: mutation.isPending ? mutation.variables.taskId : null,
    assignError: mutation.error,
    assign: (taskId, toUserId) => mutation.mutate({ taskId, toUserId }),
    refetch: () => void query.refetch(),

    generate: () => generation.mutate(),
    generating: generation.isPending,
    /* `mutateAsync` REJECTS on a refusal, and an unhandled rejection in a click handler
       is a console error in development and nothing at all in production. It is caught
       here — once, at the boundary — rather than at each call site, because the error is
       already on its way to the screen through `schedule.error`. */
    setSchedule: (localMinute) =>
      schedule.mutateAsync(localMinute).then(() => true, () => false),
    savingSchedule: schedule.isPending,
    scheduleError: schedule.error,
    clearScheduleError: () => schedule.reset(),
    generated,
    generateError: generation.error,
    dismissGenerated: () => setGenerated(null),

    proposal,
    propose: () => {
      setApproveError(null)
      setProposal(query.data
        ? proposeAllocation(query.data.unassigned, query.data.lanes)
        : null)
    },
    approve: () => { if (proposal) approval.mutate(proposal) },
    discard: () => { setProposal(null); setApproveError(null) },
    approving,
    approveError,
  }
}
